import React, { useState, useEffect } from 'react';
import { Card, Typography, Form, Input, Button, Select, Upload, message, Space, Alert, Tabs, Tag, Timeline, Divider, Table, Badge, Tooltip } from 'antd';
import { UploadOutlined, SendOutlined, PhoneOutlined, MailOutlined, GlobalOutlined, MessageOutlined, VideoCameraOutlined, BookOutlined, ClockCircleOutlined, CheckCircleOutlined, EyeOutlined, MessageOutlined as MessageIcon } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import api from '../../utils/axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;


interface SupportRequest {
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  attachments: UploadFile[];
  contactEmail?: string;
  phoneNumber?: string;
  preferredContact?: 'email' | 'phone' | 'portal';
}

interface UserTicket {
  id: string;
  subject: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  category: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  response?: string;
  attachments: string[];
  createdAt: string;
  updatedAt: string;
}

interface UserFeedback {
  id: string;
  type: 'bug' | 'feature' | 'improvement' | 'praise' | 'other';
  rating: number;
  title: string;
  description: string;
  category: string;
  status: 'SUBMITTED' | 'REVIEWED' | 'IMPLEMENTED' | 'REJECTED';
  response?: string;
  createdAt: string;
  updatedAt: string;
}

const Support: React.FC = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [activeTab, setActiveTab] = useState('contact');
  const [userTickets, setUserTickets] = useState<UserTicket[]>([]);
  const [userFeedback, setUserFeedback] = useState<UserFeedback[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<UserTicket | null>(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);

  const handleSubmit = async (values: SupportRequest) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('subject', values.subject);
      formData.append('description', values.description);
      formData.append('priority', values.priority);
      formData.append('category', values.category);
      formData.append('contactEmail', values.contactEmail || '');
      formData.append('phoneNumber', values.phoneNumber || '');
      formData.append('preferredContact', values.preferredContact || 'email');
      
      if (values.attachments && values.attachments.length > 0) {
        values.attachments.forEach(file => {
          if (file.originFileObj) {
            formData.append('attachments', file.originFileObj);
          }
        });
      }

      const response = await api.post('/support', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        message.success('Support request submitted successfully! We\'ll get back to you within 24 hours.');
        form.resetFields();
        setFileList([]);
      } else {
        message.error('Failed to submit support request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting support request:', error);
      message.error('Failed to submit support request. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const fetchUserTickets = async () => {
    setLoadingTickets(true);
    try {
      const [ticketsResponse, feedbackResponse] = await Promise.all([
        api.get('/support/my-tickets'),
        api.get('/feedback/my-feedback')
      ]);
      
      if (ticketsResponse.data.success) {
        setUserTickets(ticketsResponse.data.data);
      }
      
      if (feedbackResponse.data.success) {
        setUserFeedback(feedbackResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching user tickets:', error);
      message.error('Failed to load your tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'my-tickets') {
      fetchUserTickets();
    }
  }, [activeTab]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
      case 'URGENT':
        return 'red';
      case 'high':
      case 'HIGH':
        return 'orange';
      case 'medium':
      case 'MEDIUM':
        return 'blue';
      case 'low':
      case 'LOW':
        return 'green';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
      case 'SUBMITTED':
        return 'warning';
      case 'IN_PROGRESS':
      case 'REVIEWED':
        return 'processing';
      case 'RESOLVED':
      case 'IMPLEMENTED':
        return 'success';
      case 'CLOSED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const supportChannels = [
    {
      title: '24/7 Emergency Support',
      description: 'For critical issues affecting claim processing',
      phone: '1-800-PA-URGENT',
      email: 'emergency@pacrm.com',
      response: 'Immediate response',
      icon: <PhoneOutlined className="text-red-500" />,
      color: 'red'
    },
    {
      title: 'Technical Support',
      description: 'For system issues and technical problems',
      phone: '1-800-PA-TECH',
      email: 'tech@pacrm.com',
      response: 'Within 4 hours',
      icon: <MessageOutlined className="text-blue-500" />,
      color: 'blue'
    },
    {
      title: 'Account & Billing',
      description: 'For account management and billing questions',
      phone: '1-800-PA-BILL',
      email: 'billing@pacrm.com',
      response: 'Within 24 hours',
      icon: <MailOutlined className="text-green-500" />,
      color: 'green'
    },
    {
      title: 'Training & Onboarding',
      description: 'For new user training and feature questions',
      phone: '1-800-PA-TRAIN',
      email: 'training@pacrm.com',
      response: 'Within 48 hours',
      icon: <BookOutlined className="text-purple-500" />,
      color: 'purple'
    }
  ];

  const quickSolutions = [
    {
      title: 'Can\'t log in?',
      solutions: [
        'Check your internet connection',
        'Clear browser cache and cookies',
        'Use the "Forgot Password" link',
        'Try a different browser',
        'Contact support if issues persist'
      ]
    },
    {
      title: 'Upload not working?',
      solutions: [
        'Check file size (max 50MB per file)',
        'Ensure file type is supported',
        'Try uploading one file at a time',
        'Check your internet connection',
        'Clear browser cache'
      ]
    },
    {
      title: 'Mobile app issues?',
      solutions: [
        'Update to the latest version',
        'Restart your device',
        'Check app permissions',
        'Reinstall the app',
        'Contact mobile support'
      ]
    },
    {
      title: 'Report generation problems?',
      solutions: [
        'Check your data permissions',
        'Try a smaller date range',
        'Clear browser cache',
        'Use a different browser',
        'Contact technical support'
      ]
    }
  ];

  return (
    <div className="p-6">
      <Card>
        <div className="mb-6">
          <Title level={3}>Support Center</Title>
          <Text type="secondary">
            Get help when you need it. Our support team is here to assist you with any questions or issues.
          </Text>
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab} 
          size="large"
          items={[
            {
              key: 'contact',
              label: 'Contact Support',
              children: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <Title level={4}>Submit a Support Request</Title>
                    <Text className="block mb-6">
                      Fill out the form below and we'll get back to you as soon as possible. 
                      For urgent issues, please call our emergency support line.
                    </Text>

                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSubmit}
                    >
                      <Form.Item
                        name="subject"
                        label="Subject"
                        rules={[{ required: true, message: 'Please enter a subject' }]}
                      >
                        <Input placeholder="Brief description of your issue" size="large" />
                      </Form.Item>

                      <Form.Item
                        name="category"
                        label="Category"
                        rules={[{ required: true, message: 'Please select a category' }]}
                      >
                        <Select placeholder="Select a category" size="large">
                          <Option value="technical">Technical Issue</Option>
                          <Option value="billing">Billing Question</Option>
                          <Option value="feature">Feature Request</Option>
                          <Option value="account">Account Issue</Option>
                          <Option value="training">Training Request</Option>
                          <Option value="bug">Bug Report</Option>
                          <Option value="other">Other</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="priority"
                        label="Priority"
                        rules={[{ required: true, message: 'Please select a priority' }]}
                      >
                        <Select placeholder="Select priority" size="large">
                          <Option value="low">Low - General question</Option>
                          <Option value="medium">Medium - Feature request</Option>
                          <Option value="high">High - System issue</Option>
                          <Option value="urgent">Urgent - Critical problem</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please provide a description' }]}
                      >
                        <TextArea
                          rows={6}
                          placeholder="Please provide detailed information about your issue. Include steps to reproduce, error messages, and any relevant screenshots."
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="contactEmail"
                        label="Contact Email"
                        rules={[
                          { required: true, message: 'Please enter your email address' },
                          { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                      >
                        <Input placeholder="Enter your email address" size="large" />
                      </Form.Item>

                      <Form.Item
                        name="phoneNumber"
                        label="Phone Number (Optional)"
                      >
                        <Input placeholder="Enter your phone number" size="large" />
                      </Form.Item>

                      <Form.Item
                        name="preferredContact"
                        label="Preferred Contact Method"
                      >
                        <Select placeholder="Select preferred contact method" size="large">
                          <Option value="email">Email</Option>
                          <Option value="phone">Phone</Option>
                          <Option value="portal">Support Portal</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="attachments"
                        label="Attachments (Optional)"
                        valuePropName="fileList"
                        getValueFromEvent={e => e.fileList}
                      >
                        <div>
                          <Upload
                            listType="text"
                            fileList={fileList}
                            onChange={handleFileChange}
                            beforeUpload={() => false}
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                          >
                            <Button icon={<UploadOutlined />} size="large">
                              Upload Files
                            </Button>
                          </Upload>
                          <Text type="secondary">
                            Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, TXT (Max 50MB per file)
                          </Text>
                        </div>
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          icon={<SendOutlined />}
                          loading={submitting}
                          size="large"
                          block
                        >
                          Submit Request
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>

                  <div>
                    <Title level={4}>Support Channels</Title>
                    
                    <Alert
                      message="Emergency Support Available 24/7"
                      description="For critical issues affecting claim processing, call our emergency line immediately."
                      type="warning"
                      showIcon
                      className="mb-6"
                    />

                    <div className="space-y-4">
                      {supportChannels.map((channel, index) => (
                        <Card key={index} size="small" className="border-l-4" style={{ borderLeftColor: channel.color }}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start">
                              <div className="mr-3 mt-1">
                                {channel.icon}
                              </div>
                              <div>
                                <Text strong className="block">{channel.title}</Text>
                                <Text type="secondary" className="block text-sm">{channel.description}</Text>
                                <div className="mt-2 space-y-1">
                                  <div className="flex items-center">
                                    <PhoneOutlined className="mr-2 text-gray-500" />
                                    <Text className="text-sm">{channel.phone}</Text>
                                  </div>
                                  <div className="flex items-center">
                                    <MailOutlined className="mr-2 text-gray-500" />
                                    <Text className="text-sm">{channel.email}</Text>
                                  </div>
                                  <div className="flex items-center">
                                    <ClockCircleOutlined className="mr-2 text-gray-500" />
                                    <Text className="text-sm">Response: {channel.response}</Text>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <Tag color={channel.color} size="small">
                              {channel.response}
                            </Tag>
                          </div>
                        </Card>
                      ))}
                    </div>

                    <Divider />

                    <Title level={5}>Support Hours</Title>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Text>Monday - Friday</Text>
                        <Text strong>8:00 AM - 8:00 PM EST</Text>
                      </div>
                      <div className="flex justify-between">
                        <Text>Saturday</Text>
                        <Text strong>9:00 AM - 6:00 PM EST</Text>
                      </div>
                      <div className="flex justify-between">
                        <Text>Sunday</Text>
                        <Text strong>10:00 AM - 4:00 PM EST</Text>
                      </div>
                      <div className="flex justify-between">
                        <Text>Emergency Support</Text>
                        <Text strong className="text-red-500">24/7 Available</Text>
                      </div>
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'solutions',
              label: 'Quick Solutions',
              children: (
                <div className="space-y-6">
                  <Title level={4}>Common Issues & Solutions</Title>
                  
                  {quickSolutions.map((solution, index) => (
                    <Card key={index} size="small">
                      <Title level={5} className="mb-3">{solution.title}</Title>
                      <ul className="space-y-2">
                        {solution.solutions.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start">
                            <CheckCircleOutlined className="mr-2 mt-1 text-green-500" />
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ))}
                </div>
              )
            },
            {
              key: 'resources',
              label: 'Support Resources',
              children: (
                <div className="space-y-6">
                  <Title level={4}>Additional Support Resources</Title>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Card size="small">
                      <div className="text-center">
                        <BookOutlined className="text-3xl text-blue-500 mb-2" />
                        <Title level={5}>Documentation</Title>
                        <Text type="secondary" className="block mb-3">
                          Complete guides and tutorials
                        </Text>
                        <Button type="primary" size="small">
                          View Documentation
                        </Button>
                      </div>
                    </Card>

                    <Card size="small">
                      <div className="text-center">
                        <VideoCameraOutlined className="text-3xl text-green-500 mb-2" />
                        <Title level={5}>Video Tutorials</Title>
                        <Text type="secondary" className="block mb-3">
                          Step-by-step video guides
                        </Text>
                        <Button type="primary" size="small">
                          Watch Videos
                        </Button>
                      </div>
                    </Card>

                    <Card size="small">
                      <div className="text-center">
                        <MessageOutlined className="text-3xl text-purple-500 mb-2" />
                        <Title level={5}>FAQ</Title>
                        <Text type="secondary" className="block mb-3">
                          Frequently asked questions
                        </Text>
                        <Button type="primary" size="small">
                          Browse FAQ
                        </Button>
                      </div>
                    </Card>

                    <Card size="small">
                      <div className="text-center">
                        <GlobalOutlined className="text-3xl text-orange-500 mb-2" />
                        <Title level={5}>Support Portal</Title>
                        <Text type="secondary" className="block mb-3">
                          Track your support tickets
                        </Text>
                        <Button type="primary" size="small">
                          Access Portal
                        </Button>
                      </div>
                    </Card>

                    <Card size="small">
                      <div className="text-center">
                        <PhoneOutlined className="text-3xl text-red-500 mb-2" />
                        <Title level={5}>Live Chat</Title>
                        <Text type="secondary" className="block mb-3">
                          Real-time chat support
                        </Text>
                        <Button type="primary" size="small">
                          Start Chat
                        </Button>
                      </div>
                    </Card>

                    <Card size="small">
                      <div className="text-center">
                        <CheckCircleOutlined className="text-3xl text-green-500 mb-2" />
                        <Title level={5}>System Status</Title>
                        <Text type="secondary" className="block mb-3">
                          Check system availability
                        </Text>
                        <Button type="primary" size="small">
                          Check Status
                        </Button>
                      </div>
                    </Card>
                  </div>

                  <Divider />

                  <Title level={5}>Support Timeline</Title>
                  <Timeline>
                    <Timeline.Item color="green">
                      <Text strong>Submit Request</Text>
                      <br />
                      <Text type="secondary">Fill out the support form with detailed information</Text>
                    </Timeline.Item>
                    <Timeline.Item color="blue">
                      <Text strong>Initial Response</Text>
                      <br />
                      <Text type="secondary">Receive confirmation within 1 hour</Text>
                    </Timeline.Item>
                    <Timeline.Item color="orange">
                      <Text strong>Investigation</Text>
                      <br />
                      <Text type="secondary">Our team investigates and works on your issue</Text>
                    </Timeline.Item>
                    <Timeline.Item color="green">
                      <Text strong>Resolution</Text>
                      <br />
                      <Text type="secondary">Receive solution and follow-up communication</Text>
                    </Timeline.Item>
                  </Timeline>
                </div>
              )
            },
            {
              key: 'my-tickets',
              label: 'My Tickets',
              children: (
                <div className="space-y-6">
                  <Title level={4}>My Support Tickets & Feedback</Title>
                  
                  {loadingTickets ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                      <Text className="block mt-2">Loading your tickets...</Text>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Support Tickets */}
                      <div>
                        <Title level={5}>Support Tickets ({userTickets.length})</Title>
                        {userTickets.length === 0 ? (
                          <Card>
                            <div className="text-center py-4">
                              <MessageIcon className="text-4xl text-gray-400 mb-2" />
                              <Text type="secondary">No support tickets found</Text>
                              <br />
                              <Text type="secondary">Submit a support request to get started</Text>
                            </div>
                          </Card>
                        ) : (
                          <div className="space-y-3">
                            {userTickets.map((ticket) => (
                              <Card key={ticket.id} size="small" hoverable>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Text strong>{ticket.subject}</Text>
                                      <Tag color={getPriorityColor(ticket.priority)} size="small">
                                        {ticket.priority}
                                      </Tag>
                                      <Tag color={getStatusColor(ticket.status)} size="small">
                                        {ticket.status}
                                      </Tag>
                                    </div>
                                    <Text type="secondary" className="block mb-2">
                                      {ticket.description.substring(0, 100)}...
                                    </Text>
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                      <span>Category: {ticket.category}</span>
                                      <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                                      {ticket.attachments.length > 0 && (
                                        <span>📎 {ticket.attachments.length} attachment(s)</span>
                                      )}
                                    </div>
                                    {ticket.response && (
                                      <div className="mt-3 p-3 bg-blue-50 rounded">
                                        <Text strong className="text-blue-700">Response:</Text>
                                        <Text className="block mt-1">{ticket.response}</Text>
                                      </div>
                                    )}
                                  </div>
                                  <Tooltip title="View Details">
                                    <Button
                                      type="text"
                                      icon={<EyeOutlined />}
                                      onClick={() => {
                                        setSelectedTicket(ticket);
                                        setTicketDialogOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Feedback */}
                      <div>
                        <Title level={5}>Feedback ({userFeedback.length})</Title>
                        {userFeedback.length === 0 ? (
                          <Card>
                            <div className="text-center py-4">
                              <MessageIcon className="text-4xl text-gray-400 mb-2" />
                              <Text type="secondary">No feedback submitted</Text>
                              <br />
                              <Text type="secondary">Share your thoughts to help us improve</Text>
                            </div>
                          </Card>
                        ) : (
                          <div className="space-y-3">
                            {userFeedback.map((feedback) => (
                              <Card key={feedback.id} size="small" hoverable>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Text strong>{feedback.title}</Text>
                                      <Tag color={getStatusColor(feedback.status)} size="small">
                                        {feedback.status}
                                      </Tag>
                                      <Badge count={feedback.rating} showZero style={{ backgroundColor: '#52c41a' }} />
                                    </div>
                                    <Text type="secondary" className="block mb-2">
                                      {feedback.description.substring(0, 100)}...
                                    </Text>
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                      <span>Type: {feedback.type}</span>
                                      <span>Category: {feedback.category}</span>
                                      <span>Submitted: {new Date(feedback.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    {feedback.response && (
                                      <div className="mt-3 p-3 bg-green-50 rounded">
                                        <Text strong className="text-green-700">Response:</Text>
                                        <Text className="block mt-1">{feedback.response}</Text>
                                      </div>
                                    )}
                                  </div>
                                  <Tooltip title="View Details">
                                    <Button
                                      type="text"
                                      icon={<EyeOutlined />}
                                      onClick={() => {
                                        setSelectedTicket(feedback as any);
                                        setTicketDialogOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default Support; 